import store from '@/store';
import {deepCopyObject} from '@/utils/portuna';
import {type ComponentPublicInstance, reactive, toRaw, watch} from 'vue';
import {unwatchResize, watchResize} from './onResize';
import {isEmptyObject} from './utils';

const breakpoints = {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 1024,
    xl: 1400,
    xxl: 1650,
};

const maxSize = 99999;

const sizes: Record<string, (width: number) => boolean> = {
    'screen-xs': w => breakpoints.xs <= w && w < breakpoints.sm,
    'screen-xs-down': w => w < breakpoints.sm,
    'screen-xs-up': w => breakpoints.xs <= w,
    'screen-sm': w => breakpoints.sm <= w && w < breakpoints.md,
    'screen-sm-down': w => w < breakpoints.md,
    'screen-sm-up': w => breakpoints.sm <= w,
    'screen-md': w => breakpoints.md <= w && w < breakpoints.lg,
    'screen-md-down': w => w < breakpoints.lg,
    'screen-md-up': w => breakpoints.md <= w,
    'screen-lg': w => breakpoints.lg <= w && w < breakpoints.xl,
    'screen-lg-down': w => w < breakpoints.xl,
    'screen-lg-up': w => breakpoints.lg <= w,
    'screen-xl': w => breakpoints.xl <= w && w < breakpoints.xxl,
    'screen-xl-down': w => w < breakpoints.xxl,
    'screen-xl-up': w => breakpoints.xl <= w,
    'screen-xxl': w => breakpoints.xxl <= w,
    'screen-xxl-down': w => w <= maxSize,
    'screen-xxl-up': w => breakpoints.xxl <= w && w <= maxSize,
};

interface MediaQueryCallback
{
    match: VoidFunction
    unmatch: VoidFunction
    destruct?: Nullable<VoidFunction>
}

interface MediaMatchCallbacks
{
    callbacksMatch: Record<string, VoidFunction>
    callbacksUnmatch: Record<string, VoidFunction>
}

type MediaElement = HTMLElement | Element | HTMLBodyElement;

export default class MediaQueryHandler
{
    static _rootElement: Nullable<MediaElement> = null;

    static _observers: MediaObserver[] = [];

    static on(query: MediaQueryHandlerQuery, callbacks: MediaQueryCallback, element: Nullable<MediaElement> = null): string
    {
        if (!sizes[query])
        {
            throw new Error('no query with name ' + query + ' found');
        }

        const uuid     = Date.now().toString() + '-' + this._observers.length; // TODO generate uuid
        const observer = this._getObserverObject(element);

        observer.addSizeListener(query, uuid, callbacks);

        return uuid;
    }

    static _getObserverObject(element: Nullable<MediaElement> = null): MediaObserver
    {
        if (!element)
        {
            element = this._rootElement || document.querySelector('body');
        }

        let observer: Nullable<MediaObserver> = null;

        for (let _observer of this._observers)
        {
            if (_observer && _observer.element.isSameNode(element))
            {
                observer = _observer;
                break;
            }
        }

        if (!observer)
        {
            observer = new MediaObserver(element!);
            this._observers.push(observer!);
        }

        return observer;
    }

    static remove(query: MediaQueryHandlerQuery, uuid: string): void
    {
        for (let i = 0; i <= this._observers.length; i++)
        {
            const observer = this._observers[i];

            observer.removeQueryById(query, uuid);

            if (!observer.sizeCallbacks.length)
            {
                observer.destruct();
                this._observers.splice(i--, 1);
            }
        }
    }

    static removeAllByIds(uuids: string[]): void
    {
        for (let uuid of uuids)
        {
            this.removeAll(uuid);
        }
    }

    static removeAll(uuid: string): void
    {
        for (let i = 0; i < this._observers.length; i++)
        {
            const observer = this._observers[i];

            observer.removeQueriesById(uuid);

            if (isEmptyObject(observer.sizeCallbacks))
            {
                observer.destruct();
                this._observers.splice(i--, 1);
            }
        }
    }

    static setRootElement(element: MediaElement): void
    {
        this._rootElement = element;
    }

    static hasRootElement(): boolean
    {
        return !!this._rootElement;
    }
}

class MediaObserver
{
    observer: PortUNAResizeObserver;

    element: MediaElement;

    sizeCallbacks: Record<string, MediaMatchCallbacks> = {};

    destructCallbacks: Record<string, VoidFunction> = {};

    constructor(element: MediaElement)
    {
        this.element = element;

        this.observer = watchResize(this.element as HTMLElement, () =>
        {
            for (let size in this.sizeCallbacks)
            {
                if (this.sizeCallbacks.hasOwnProperty(size))
                {
                    const sizeCallbacks = this.sizeCallbacks[size];
                    const callbacks     = sizes[size](this.element.clientWidth) ? sizeCallbacks.callbacksMatch : sizeCallbacks.callbacksUnmatch;

                    for (let key in callbacks)
                    {
                        if (callbacks.hasOwnProperty(key))
                        {
                            callbacks[key]();
                        }
                    }
                }
            }
        },
        this.destruct);
    }

    destruct()
    {
        for (let key in this.destructCallbacks)
        {
            if (this.destructCallbacks.hasOwnProperty(key))
            {
                this.destructCallbacks[key]();
            }
        }

        unwatchResize(this.observer);
    }

    addSizeListener(query: MediaQueryHandlerQuery, uuid: string, callbacks: MediaQueryCallback)
    {
        let queryObject = this._getQueryObject(query);

        queryObject.callbacksMatch[uuid]   = callbacks.match;
        queryObject.callbacksUnmatch[uuid] = callbacks.unmatch;

        if (callbacks.destruct)
        {
            this.destructCallbacks[uuid] = callbacks.destruct;
        }

        this.observer.triggerCallback();
    }

    removeQueryById(query: MediaQueryHandlerQuery, uuid: string): MediaMatchCallbacks
    {
        let queryObject = this._getQueryObject(query);

        delete queryObject.callbacksMatch[uuid];
        delete queryObject.callbacksUnmatch[uuid];

        return queryObject;
    }

    removeQueriesById(uuid: string): void
    {
        for (const size in this.sizeCallbacks)
        {
            if (this.sizeCallbacks.hasOwnProperty(size))
            {
                let queryObject = this.removeQueryById(<MediaQueryHandlerQuery>size, uuid);
                if (isEmptyObject(queryObject.callbacksMatch) && isEmptyObject(queryObject.callbacksUnmatch))
                {
                    delete this.sizeCallbacks[size];
                }
            }
        }
    }

    private _getQueryObject(query: MediaQueryHandlerQuery): MediaMatchCallbacks
    {
        if (!this.sizeCallbacks[query])
        {
            this.sizeCallbacks[query] = {
                callbacksMatch: {},
                callbacksUnmatch: {},
            };
        }

        return this.sizeCallbacks[query];
    }
}

export function addMediaQueryHandlers(screen: Record<string, boolean>, element: Nullable<MediaElement> = null, destructCallback: Nullable<VoidFunction> = null): string[]
{
    let mediaQueryUuids = [];
    const replaceRegEx  = new RegExp('[A-Z]', 'g');

    for (let size in screen)
    {
        if (screen.hasOwnProperty(size))
        {
            let query = 'screen-' + size.replace(replaceRegEx, char => `-${char.toLowerCase()}`) as MediaQueryHandlerQuery;
            let uuId  = MediaQueryHandler.on(query, {
                match: () =>
                {
                    screen[size] = true;
                },
                unmatch: () =>
                {
                    screen[size] = false;
                },
                destruct: destructCallback,
            }, element);

            mediaQueryUuids.push(uuId);
        }
    }

    return mediaQueryUuids;
}

export function getSizeClass(sizes: Record<string, boolean>, prefix = '')
{
    let className = prefix + 'view-size-lg';

    for (let size in sizes)
    {
        if (sizes.hasOwnProperty(size) && size.length <= 3 && sizes[size])
        {
            className = prefix + 'view-size-' + size;
        }
    }

    return className;
}

export function initializeGrid(vueInstance: ComponentPublicInstance, element: Nullable<HTMLElement | Element> = null)
{
    if (!MediaQueryHandler.hasRootElement() && element)
    {
        element = element || vueInstance.$el;
        MediaQueryHandler.setRootElement(element);
        addMediaQueryHandlers(vueInstance.viewSize, element);

        const viewSize = reactive(deepCopyObject(store.state.viewSize));
        watch(() => viewSize, (data) => store.dispatch('updateViewSize', toRaw(data)), {deep: true});
        addMediaQueryHandlers(viewSize, element);
    }
}